/**
 * App root — M1 auth + M2 portfolio + M3 workspace/import + M4 schedule + M5 status routing.
 *
 * Provider tree: QueryClientProvider → RouterProvider → Toaster
 *
 * Routes:
 *  /login               — LoginPage (public)
 *  /change-password     — ChangePasswordPage (public-ish; force-reset enforced by RequireAuth)
 *  /                    — AppShell + DashboardPage (RequireAuth guarded) — GLOBAL default dashboard
 *  /accounts            — AppShell + AccountsPage (RequireAuth guarded)
 *  /account/:id         — AppShell + DashboardPage (RequireAuth guarded) — ACCOUNT-scoped dashboard
 *  /project/:id         — AppShell + ProjectWorkspacePage (RequireAuth guarded)
 *  /schedule            — AppShell + AutoSchedulePage (RequireAuth guarded)
 *  /resources-calendar  — AppShell + ResourcesCalendarPage (RequireAuth guarded)
 *  /support             — AppShell + SupportPage (RequireAuth guarded)
 *  /settings            — AppShell + SettingsPage (RequireAuth guarded; ADMIN-only content)
 *  *                    — redirect to / (guarded)
 *
 * RequireAuth enforces:
 *  - No accessToken → /login
 *  - mustChangePassword=true + not on /change-password → /change-password
 */
import { useEffect, useState, lazy, Suspense } from 'react';
import type { ReactNode } from 'react';
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router';
import { Toaster, toast } from 'sonner';
import { bootstrapSession } from './api/auth';
import { SessionSplash } from './components/SessionSplash';
import { AppShell } from './components/AppShell';
import { RouteSkeleton } from './components/RouteSkeleton';
import { LoginPage } from './pages/LoginPage';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { DashboardPage } from './pages/DashboardPage';
import { RequireAuth } from './routes/RequireAuth';
import { MUTATION_ERROR_TOAST_ID } from './lib/toastIds';

// Heavy non-landing pages are route-level React.lazy chunks — the dashboard
// (DashboardPage, imported statically above) is the app's first authenticated
// paint and stays eager; everything else only pays its bundle cost when a
// user actually navigates there. This is what keeps @xyflow/react (pulled in
// only by AutoSchedulePage) out of the entry chunk.
const AccountsPage = lazy(() => import('./pages/AccountsPage').then((m) => ({ default: m.AccountsPage })));
const ProjectWorkspacePage = lazy(() => import('./pages/ProjectWorkspacePage').then((m) => ({ default: m.ProjectWorkspacePage })));
const AutoSchedulePage = lazy(() => import('./pages/AutoSchedulePage').then((m) => ({ default: m.AutoSchedulePage })));
const ResourcesCalendarPage = lazy(() => import('./pages/ResourcesCalendarPage').then((m) => ({ default: m.ResourcesCalendarPage })));
const WorkloadCapacityPage = lazy(() => import('./pages/WorkloadCapacityPage').then((m) => ({ default: m.WorkloadCapacityPage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const SupportPage = lazy(() => import('./pages/SupportPage').then((m) => ({ default: m.SupportPage })));

// Re-exported for any caller that still reaches for the id off '@/App'.
// The id itself lives in lib/toastIds.ts (a dependency-free leaf module) —
// App.tsx's router transitively imports nearly every page/component, so a
// component importing the id FROM here would close an import cycle; new
// per-call toast.error({ id }) sites should import it from lib/toastIds
// directly instead (see that file for the full rationale).
export { MUTATION_ERROR_TOAST_ID };
const MUTATION_ERROR_FALLBACK_MESSAGE = "Something went wrong — your change wasn't saved.";

// queryClient is exported alongside the App component so App.test.tsx can
// exercise the real production MutationCache config directly rather than
// re-implementing it; App.tsx is the root module (never itself an HMR
// boundary a component re-exports through), so this doesn't cost Fast
// Refresh anything.
// eslint-disable-next-line react-refresh/only-export-components
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 2 * 60 * 1000,
    },
  },
  // Global silent-failure backstop: any mutation that rejects without its
  // own error handling would otherwise fail with no user-visible feedback.
  // `mutation.options.onError` reflects a hook-level `useMutation({ onError })`
  // — the one signal TanStack Query exposes here — so a mutation that has
  // taken responsibility for its own error UI is left alone; everything else
  // gets this generic toast. (Per-call `mutate(vars, { onError })` handlers
  // are NOT visible on `mutation.options` — TanStack keeps those private to
  // the calling observer — so call sites that want specific copy AND to
  // suppress a redundant generic toast use the shared
  // MUTATION_ERROR_TOAST_ID above so their toast overwrites this one in
  // place; it always fires after this callback within the same mutation
  // lifecycle.)
  //
  // A third opt-out exists for mutations that want NO toast at all (not even
  // an overwritten one) because the caller's own UI already communicates the
  // failure — e.g. an inline form error, or a flow whose local state change
  // proceeds regardless of the network result. Those set
  // `meta: { suppressGlobalErrorToast: true }` (see src/api/auth.ts for the
  // three call sites) rather than a no-op `onError`, so the intent reads at
  // the mutation definition instead of as an empty callback.
  mutationCache: new MutationCache({
    onError: (_error, _variables, _context, mutation) => {
      if (mutation.options.onError) return;
      if (mutation.options.meta?.suppressGlobalErrorToast) return;
      toast.error(MUTATION_ERROR_FALLBACK_MESSAGE, { id: MUTATION_ERROR_TOAST_ID });
    },
  }),
});

const routes = [
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/change-password',
    element: <ChangePasswordPage />,
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppShell active="dashboard" title="Portfolio Dashboard" accountScoped>
          <DashboardPage />
        </AppShell>
      </RequireAuth>
    ),
  },
  {
    path: '/accounts',
    element: (
      <RequireAuth>
        <AppShell active="accounts" title="Accounts">
          <Suspense fallback={<RouteSkeleton />}>
            <AccountsPage />
          </Suspense>
        </AppShell>
      </RequireAuth>
    ),
  },
  {
    path: '/account/:id',
    element: (
      <RequireAuth>
        <AppShell active="accounts" title="Account">
          <DashboardPage />
        </AppShell>
      </RequireAuth>
    ),
  },
  {
    path: '/project/:id',
    element: (
      <RequireAuth>
        <AppShell active="" title="Project Workspace">
          <Suspense fallback={<RouteSkeleton />}>
            <ProjectWorkspacePage />
          </Suspense>
        </AppShell>
      </RequireAuth>
    ),
  },
  {
    path: '/schedule',
    element: (
      <RequireAuth>
        <AppShell active="schedule" title="Auto-Schedule" accountScoped>
          <Suspense fallback={<RouteSkeleton />}>
            <AutoSchedulePage />
          </Suspense>
        </AppShell>
      </RequireAuth>
    ),
  },
  {
    path: '/resources-calendar',
    element: (
      <RequireAuth>
        <AppShell active="resources-calendar" title="Resources & Calendar" accountScoped>
          <Suspense fallback={<RouteSkeleton />}>
            <ResourcesCalendarPage />
          </Suspense>
        </AppShell>
      </RequireAuth>
    ),
  },
  {
    path: '/workload',
    element: (
      <RequireAuth>
        <AppShell active="capacity" title="Workload & Capacity" accountScoped>
          <Suspense fallback={<RouteSkeleton />}>
            <WorkloadCapacityPage />
          </Suspense>
        </AppShell>
      </RequireAuth>
    ),
  },
  {
    path: '/support',
    element: (
      <RequireAuth>
        <AppShell active="support" title="Support">
          <Suspense fallback={<RouteSkeleton />}>
            <SupportPage />
          </Suspense>
        </AppShell>
      </RequireAuth>
    ),
  },
  {
    path: '/settings',
    element: (
      <RequireAuth>
        <AppShell active="settings" title="Settings">
          <Suspense fallback={<RouteSkeleton />}>
            <SettingsPage />
          </Suspense>
        </AppShell>
      </RequireAuth>
    ),
  },
  {
    path: '*',
    element: (
      <RequireAuth>
        <Navigate to="/" replace />
      </RequireAuth>
    ),
  },
];

/**
 * SessionGate — runs the one-shot session bootstrap before the router renders.
 * The access token is in-memory only, so a hard reload would otherwise bounce
 * to /login even though the httpOnly refresh cookie is still valid. We restore
 * the token from that cookie first, showing the SessionSplash until it settles,
 * so deep-links and reloads land on the page the user was on. Mounted once
 * above the router; client-side navigation never re-runs it.
 */
function SessionGate({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let mounted = true;
    bootstrapSession().finally(() => {
      if (mounted) setReady(true);
    });
    return () => {
      mounted = false;
    };
  }, []);

  if (!ready) return <SessionSplash />;
  return <>{children}</>;
}

export function App() {
  // Create the router once per App instance (lazy init). Scoping it to the
  // component instead of module scope keeps it stable across re-renders while
  // staying deterministic in tests (each mount reads the current location).
  const [router] = useState(() => createBrowserRouter(routes));

  return (
    <QueryClientProvider client={queryClient}>
      <SessionGate>
        <RouterProvider router={router} />
      </SessionGate>
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-sm)',
            background: 'var(--surface)',
            color: 'var(--text)',
            border: '1px solid var(--border)',
          },
        }}
      />
    </QueryClientProvider>
  );
}
